import React from "react";
import "./BalanceBankChip.css";
import { t } from "../i18n";

// The chip at the bottom right of the Balance card. Once Instant Payouts are
// set up it gets a gold "⚡ INSTANT" half (founder pick B2, build 44) and the
// Instant Payouts row under the card goes away: the gold half opens the same
// explainer popup the row did, the number still opens Stripe. Not set up →
// today's chip, with a drawn bank instead of the 🏦 emoji.

type Props = {
  last4: string | null;
  brand: string | null;
  /** Instant Payouts set up: the rules pass and Stripe has somewhere to pay instantly. */
  instant: boolean;
  /** The Stripe link is opening. */
  busy: boolean;
  onOpenStripe: () => void;
  onOpenInstant: () => void;
  /** Paid through MassPay (ID check passed): the chip opens their MassPay wallet instead. */
  masspay?: boolean;
  onOpenMasspay?: () => void;
};

const BANK = (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M3 10 12 4l9 6" />
    <path d="M5 10v8M9.5 10v8M14.5 10v8M19 10v8" />
    <path d="M3 20h18" />
  </svg>
);

// Brand names read the same in every language.
const MASSPAY = "MassPay";

const WALLET = (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M19 7V5a2 2 0 0 0-2-2H5a2 2 0 0 0 0 4h14a2 2 0 0 1 2 2v3" />
    <path d="M3 5v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-3" />
    <path d="M17 12h4v4h-4a2 2 0 0 1 0-4z" />
  </svg>
);

const BOLT = (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M13.5 2 4.5 13.5H11L10 22l9-11.5h-6.5L13.5 2Z" />
  </svg>
);

export default function BalanceBankChip({ last4, brand, instant, busy, onOpenStripe, onOpenInstant, masspay = false, onOpenMasspay }: Props) {
  // Paid through MassPay: one chip, their MassPay wallet (where they withdraw).
  if (masspay) {
    return (
      <span className="bank-chip">
        <button type="button" className="bank-chip-num" onClick={onOpenMasspay} title={t("balancePage.openMasspay")}>
          {WALLET}
          {MASSPAY} <span aria-hidden="true">›</span>
        </button>
      </span>
    );
  }
  // No bank on file and nothing instant to show → no chip (as before).
  if (!last4 && !instant) return null;
  return (
    <span className="bank-chip">
      {instant && (
        <button
          type="button"
          className="bank-chip-instant"
          aria-haspopup="dialog"
          aria-label={t("balancePage.instantPayouts")}
          title={t("balancePage.instantPayouts")}
          disabled={busy}
          onClick={onOpenInstant}
        >
          {BOLT}
          {t("balancePage.instantBadge")}
        </button>
      )}
      {last4 && (
        <button
          type="button"
          className="bank-chip-num"
          onClick={onOpenStripe}
          title={brand ? t("balancePage.openStripe", { brand, last4 }) : t("balancePage.bankOpenStripe", { last4 })}
        >
          {!instant && BANK}
          {busy ? t("balancePage.opening") : <>••{last4} <span aria-hidden="true">›</span></>}
        </button>
      )}
    </span>
  );
}
